import { CSSProperties, ReactElement, ReactNode } from "react";
import { Overlay, PopoverProps } from "_/components/overlay";
import { Icon, IconVariant } from "_/components/icon";
import { Button } from "_/components/button";
import * as S from "./styled";

export type MenuItem = {
  /**
   * Content to display, or a render function that returns the content to display.
   *
   * If the value provided is a function, any `icon` prop value will be ignored.
   */
  render: ReactNode | (() => ReactElement);

  /**
   * An icon variant to display alongside content.
   *
   * By default, the icon will display to the right.
   */
  icon?: IconVariant;

  /**
   * An `onClick` handler applied to the entire menu item.
   *
   * If more granular control of the click event is required within
   * the menu item area, use a `render` function with manually configured
   * event handlers.
   */
  onClick?: () => void;

  /**
   * Flag to indicate the menu item should be disabled or otherwise cannot
   * be interacted with.
   */
  disabled?: boolean;

  /**
   * Extra styling to apply to the menu item container element.
   *
   * This should only be used as an escape hatch for styling that cannot be
   * provided on the element contents, such as changing the menu item padding
   * or margin.
   */
  style?: CSSProperties;
};

type OverlayMenuProps = {
  items: ReadonlyArray<MenuItem>;
  placement?: PopoverProps["placement"];
  target: PopoverProps["target"];
  onFocus?: () => void;
};

/**
 * Predefined menu items that can be used in the overlay menu.
 */
export const MenuItems = {
  /**
   * A separator used to visually separate menu items.
   */
  SEPARATOR: {
    render: <S.Separator />,
    disabled: true,
    style: { padding: "0" },
  },
};

export const OverlayMenu = ({
  items,
  placement = "bottomStart",
  target,
  onFocus,
}: OverlayMenuProps): ReactElement => {
  return (
    <Overlay
      event="click"
      placement={placement}
      target={target}
      onFocus={onFocus}
      content={({ close }) => {
        const itemEls = items.map(
          ({ render, onClick, style, disabled = false, icon }, i) => {
            const iconEl = icon ? <Icon variant={icon} /> : null;
            const content =
              typeof render === "function" ? (
                render()
              ) : (
                <S.TextItem tabIndex={0}>
                  {render} {iconEl}
                </S.TextItem>
              );

            return (
              <S.MenuItem
                key={i}
                style={style}
                $disabled={disabled}
                onClick={() => {
                  if (disabled) return;
                  onClick?.();
                  close();
                }}
              >
                {content}
              </S.MenuItem>
            );
          }
        );

        return <S.Wrapper>{itemEls}</S.Wrapper>;
      }}
    />
  );
};

export const MeatballMenu = ({ items }: { items: MenuItem[] }) => {
  const meatBalls = (
    <Button size="small" kind="secondary" iconOnly>
      <Icon variant="MoreVert" />
    </Button>
  );

  return <OverlayMenu items={items} target={meatBalls} placement="bottomEnd" />;
};
